import StarIcon from '@mui/icons-material/Star';
import StarIconOutline from '@mui/icons-material/StarOutline';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  createTheme,
  Divider,
  FormControlLabel,
  Grid2 as Grid,
  Theme,
  ThemeProvider,
  Typography
} from '@mui/material';

import { useAuth } from 'react-oidc-context';
import { Link, useNavigate } from 'react-router-dom';
import { tss } from 'tss-react/mui';
import { ProductType } from 'types/Products';
import { COMPARE_BULK_LIMIT } from 'components/common/Search/useSelection';
import ToolTipContent from 'components/common/ToolTipContent';
import { WHITELABEL_IMAGE_PLACEHOLDER } from 'components/constants/Constants';
import { getProductPermalink } from 'helpers/getProductPermalink';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

export const SearchCard: React.FC<{
  extraProp: number;
  productKey: string;
  productType: ProductType;
  name: string;
  description: string;
  image: string | undefined;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  bookmarked: boolean;
  onBookmarkChange: (bookmarked: boolean) => void;
  selectionModeEnabled: boolean; // maybe in context
  canSelectMore: boolean; // maybe in context
}> = props => {
  const { classes } = useStyles({ productType: props.productType });

  const [bookmarked, setBookmarked] = useState(props.bookmarked);
  useEffect(() => {
    setBookmarked(props.bookmarked);
  }, [props.bookmarked]);

  const { isAuthenticated, isLoading } = useAuth();
  const notAuthenticated = isLoading || isAuthenticated === false;
  const { t } = useTranslation();

  const disabled = !props.canSelectMore && !props.selected;
  const navigate = useNavigate();

  const url = getProductPermalink(props.productType, props.productKey);

  return (
    <ThemeProvider
      theme={(theme: Theme) =>
        createTheme({
          ...theme,
          breakpoints: {
            values: {
              ...theme.breakpoints.values,
              sm: 720 // custom value here only for this special card layout
            }
          }
        })
      }
    >
      <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
        <ToolTipContent
          tooltip={disabled}
          text={`You can just compare up to ${COMPARE_BULK_LIMIT} products`}
          displayAsBlock
        >
          <Card className={classes.root}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                '&:hover': { cursor: !disabled ? 'pointer' : 'not-allowed' }
              }}
            >
              <Box
                sx={{ height: '100%', width: '100%', display: 'flex' }}
                onClick={() =>
                  props.selectionModeEnabled
                    ? !disabled
                      ? props.onSelectedChange(!props.selected)
                      : () => {}
                    : navigate(url)
                }
              >
                <CardMedia
                  component="img"
                  sx={{
                    width: 150,
                    objectFit: 'cover'
                  }}
                  image={props.image ?? WHITELABEL_IMAGE_PLACEHOLDER}
                />

                <CardContent className={classes.cardContent}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      marginBottom: 1
                    }}
                  >
                    <Typography
                      className={classes.title}
                      variant="h5"
                      gutterBottom
                    >
                      {props.name}
                    </Typography>

                    <Typography
                      variant="caption"
                      className={classes.description}
                    >
                      {props.description}
                    </Typography>
                  </Box>

                  <Box className={classes.footer}>
                    <Box>
                      <ToolTipContent
                        text={t(
                          `search.uxbeauty.favorite.tooltips.${
                            notAuthenticated
                              ? 'disabled'
                              : bookmarked
                              ? 'remove'
                              : 'add'
                          }`
                        )}
                        tooltip={true}
                      >
                        <div
                          onClick={event => {
                            event.stopPropagation();
                            if (isAuthenticated) {
                              props.onBookmarkChange(bookmarked);
                              setBookmarked(!bookmarked);
                            }
                          }}
                        >
                          {bookmarked ? (
                            <StarIcon />
                          ) : (
                            <StarIconOutline
                              sx={theme => ({
                                color: isAuthenticated
                                  ? theme.palette.primary.main
                                  : theme.palette.primary.light,
                                '&:hover': {
                                  cursor: isAuthenticated
                                    ? 'pointer'
                                    : 'not-allowed'
                                }
                              })}
                            />
                          )}
                        </div>
                      </ToolTipContent>
                    </Box>
                    <Button
                      size="small"
                      className={classes.footerButton}
                      variant="outlined"
                      component={Link}
                      to={url}
                    >
                      View more
                    </Button>
                  </Box>
                </CardContent>
              </Box>
              {props.selectionModeEnabled && (
                <>
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '100%',
                      bottom: 0,
                      bgcolor: 'white'
                    }}
                  >
                    <Divider
                      sx={theme => ({
                        borderColor: theme.palette.primary.main
                      })}
                    />
                    <ToolTipContent
                      tooltip={disabled}
                      text={`You can just compare up to ${COMPARE_BULK_LIMIT} products`}
                    >
                      <FormControlLabel
                        sx={{ ml: 0, mb: 0 }}
                        control={
                          <Checkbox
                            checked={props.selected}
                            onChange={(event, checked) => {
                              event.stopPropagation();
                              props.onSelectedChange(checked);
                            }}
                            disabled={disabled}
                          />
                        }
                        label="Select Product"
                        disabled={disabled}
                      />
                    </ToolTipContent>
                  </Box>
                </>
              )}
            </Box>
          </Card>
        </ToolTipContent>
      </Grid>
    </ThemeProvider>
  );
};

const useStyles = tss
  .withParams<{ productType: ProductType }>()
  .withNestedSelectors<'footerButton'>()
  .create(({ theme, classes, productType }) => ({
    footerButton: {
      visibility: 'hidden'
    },
    root: {
      height: 150,
      display: 'flex',
      position: 'relative',
      borderRadius: theme.spacing(1),
      [`&:hover .${classes.footerButton}`]: {
        // Using &:hover to target .footerButton
        visibility: 'visible' // when root is hovered
      },
      '&:hover:after': {
        position: 'absolute',
        pointerEvents: 'none',
        border: `2px solid ${
          productType === ProductType.FORMULATIONS ? '#E88593' : '#338BB5'
        }`,
        borderRadius: theme.spacing(1),
        top: 0,
        content: '" "',
        display: 'block',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%'
      }
    },
    cardContent: {
      justifyContent: 'space-between',
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      '&&': { paddingBottom: theme.spacing(1) }
    },

    title: {
      overflow: 'hidden',
      display: ' -webkit-box',
      WebkitLineClamp: '1',
      lineClamp: '1',
      WebkitBoxOrient: 'vertical'
    },
    description: {
      overflow: 'hidden',
      display: ' -webkit-box',
      color: theme.palette.primary.light,
      WebkitLineClamp: '2',
      lineClamp: '2',
      WebkitBoxOrient: 'vertical'
    },
    footer: {
      display: 'flex',
      gap: theme.spacing(1),
      flexDirection: 'row',
      justifyContent: 'space-between'
    }
  }));
